import { AlertCircle, Zap, MapPin, Globe } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

const HYPER_IMG_1 = 'https://customer-assets.emergentagent.com/job_simpatectomia-feira/artifacts/qkvyht70_1800x1200_hyperhidrosis_minibead.jpg.webp';
const HYPER_IMG_2 = 'https://customer-assets.emergentagent.com/job_simpatectomia-feira/artifacts/tyalfit0_drhoenle_hyperhidrose_header_03-1920x1280.jpg';

const types = [
  {
    icon: Zap,
    title: 'Primária',
    color: '#0D47A1',
    bg: '#0D47A1',
    desc: 'Hiperidrose sem causa definida, resultado de uma hiperativação do sistema nervoso simpático. Pode ser influenciada por fatores genéticos. É a melhor indicação para o tratamento cirúrgico.',
    tag: 'Melhor indicação cirúrgica',
  },
  {
    icon: AlertCircle,
    title: 'Secundária',
    color: '#00897B',
    bg: '#00897B',
    desc: 'Suor excessivo causado por uma condição médica subjacente. Causas comuns incluem distúrbios hormonais (hipertireoidismo, diabetes), medicamentos ou infecções. A condição causadora deve ser investigada.',
    tag: 'Investigação necessária',
  },
  {
    icon: MapPin,
    title: 'Localizada',
    color: '#0D47A1',
    bg: '#0D47A1',
    desc: 'Afeta áreas específicas do corpo de forma bilateral e simétrica: mãos, axilas, pés, couro cabeludo e face. Costuma ter os melhores resultados do tratamento cirúrgico.',
    tag: '',
  },
  {
    icon: Globe,
    title: 'Generalizada',
    color: '#00897B',
    bg: '#00897B',
    desc: 'Suor difuso e generalizado envolvendo todo o corpo. Pode ocorrer durante o sono e costuma ter início mais tardio (após os 25 anos).',
    tag: '',
  },
];

export default function HyperhidrosisSection() {
  const [ref, isVisible] = useScrollReveal();
  return (
    <section id="hiperidrose" ref={ref} className={`py-24 md:py-32 bg-[#EEF4FF] transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
      <div className="max-w-7xl mx-auto px-4 lg:px-8">

        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#0D47A1]/10 text-[#0D47A1] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Entenda a Condição
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            O Que É Hiperidrose
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            A Hiperidrose é uma condição caracterizada pela produção de suor em excesso, muito além da função
            fisiológica de regular a temperatura corporal. Pode afetar aproximadamente 3-5% da população.
          </p>
        </div>

        {/* Clinical Images banner */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12 rounded-2xl overflow-hidden">
          <div className="relative overflow-hidden rounded-2xl shadow-md group">
            <img
              src={HYPER_IMG_1}
              alt="Hiperidrose axilar — suor excessivo nas axilas"
              className="w-full h-64 object-cover object-center group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-4">
              <span className="text-white font-poppins font-semibold text-sm">Hiperidrose Axilar</span>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl shadow-md group">
            <img
              src={HYPER_IMG_2}
              alt="Hiperidrose palmar — suor excessivo nas mãos"
              className="w-full h-64 object-cover object-center group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-4">
              <span className="text-white font-poppins font-semibold text-sm">Hiperidrose Palmar</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {types.map((type, i) => {
            const Icon = type.icon;
            return (
              <div
                key={i}
                data-testid={`hyperhidrosis-card-${type.title.toLowerCase()}`}
                className="bg-white border border-gray-100 rounded-2xl p-8 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${type.bg}15` }}
                  >
                    <Icon size={24} style={{ color: type.color }} />
                  </div>
                  <div>
                    <h3 className="font-poppins font-bold text-xl text-[#212121] mb-1">
                      Hiperidrose {type.title}
                    </h3>
                    {type.tag && (
                      <span
                        className="text-xs font-semibold px-3 py-1 rounded-full"
                        style={{ backgroundColor: `${type.bg}15`, color: type.bg }}
                      >
                        {type.tag}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-[#212121]/70 leading-relaxed text-sm md:text-base">
                  {type.desc}
                </p>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
